import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin, ChevronRight, CheckCircle, Briefcase, Users, FileText, Monitor, Film, Truck, ArrowRight, Clock, Award, Target, Shield, ExternalLink, Linkedin, Twitter, LayoutDashboard, PieChart, ListTodo, Activity, Building2, DollarSign } from 'lucide-react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { CRMDashboard, PipelineView, ClientsList, TasksList, ActivitiesList, VendorDirectory } from './CRMComponents';
import { FinancialDashboard, InvoiceManagement, PaymentsReceived, BillsExpenses, BadDebtTracker, PLStatement } from './FinancialComponents';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const OUTLOOK_BOOKING_URL = "https://outlook.office.com/book/OpulenceLogisticsManagementCorp@NETORG6755075.onmicrosoft.com/";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_operations-hub-24/artifacts/1q6qnwcf_Transparent%20Logo.png";

// ============== NAVIGATION ==============
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
  { path: '/', label: 'Home' },
  { path: '/about', label: 'About' },
  { path: '/services', label: 'Services' },
  { path: '/booking', label: 'Book Now' },
  { path: '/contact', label: 'Contact' }];


  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'backdrop-blur-xl bg-[#1a3a6e]/90 border-b border-white/20' : 'bg-transparent'}`} data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav className="flex items-center justify-between h-32 py-4">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="h-32 w-32 overflow-hidden rounded-lg mt-4">
              <img src={LOGO_URL} alt="OLM Logo" className="h-full w-full object-cover scale-150 !mt-[5px]" />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) =>
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm font-medium transition-colors ${location.pathname === link.path ? 'text-[#FF5E00]' : 'text-white/80 hover:text-white'}`}
              data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}>

                {link.label}
              </Link>
            )}
            <a
              href={OUTLOOK_BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#FF5E00] hover:bg-[#e05300] text-white px-5 py-2.5 text-sm font-medium transition-colors flex items-center gap-2"
              data-testid="nav-schedule-call">

              Schedule a Call <ExternalLink size={14} />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-white p-2"
            data-testid="mobile-menu-toggle">

            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Mobile Navigation */}
        {isOpen &&
        <div className="md:hidden bg-[#234b8a] border-t border-white/20 py-4" data-testid="mobile-menu">
            {navLinks.map((link) =>
          <Link
            key={link.path}
            to={link.path}
            onClick={() => setIsOpen(false)}
            className={`block px-4 py-3 text-sm font-medium ${location.pathname === link.path ? 'text-[#FF5E00]' : 'text-white/80'}`}>

                {link.label}
              </Link>
          )}
            <a
            href={OUTLOOK_BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="block mx-4 mt-4 bg-[#FF5E00] text-white px-5 py-2.5 text-sm font-medium text-center">

              Schedule a Call
            </a>
          </div>
        }
      </div>
    </header>);

};

// ============== FOOTER ==============
const Footer = () =>
<footer className="bg-[#1a3a6e] border-t border-white/20 py-16" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <img src={LOGO_URL} alt="OLM Logo" className="h-24 w-auto mb-4" />
          <p className="text-[#b8c9e2] text-sm leading-relaxed max-w-md">
            Helping entrepreneurs and organizations organize, grow, and scale efficiently through strategic management, structure, and execution.
          </p>
        </div>
        
        <div>
          <h4 className="text-white font-medium mb-4">Quick Links</h4>
          <div className="space-y-2">
            {['Home', 'About', 'Services', 'Book Now', 'Contact'].map((item) =>
          <Link key={item} to={item === 'Home' ? '/' : item === 'Book Now' ? '/booking' : `/${item.toLowerCase()}`} className="block text-[#b8c9e2] hover:text-[#FF5E00] text-sm transition-colors">
                {item}
              </Link>
          )}
          </div>
        </div>
        
        <div>
          <h4 className="text-white font-medium mb-4">Contact</h4>
          <div className="space-y-3 text-[#b8c9e2] text-sm">
            <p className="flex items-start gap-2">
              <MapPin size={16} className="mt-0.5 text-[#FF5E00]" />
              1250 W Sam Houston Pkwy S<br />Suite 302, Houston, TX 77042
            </p>
            <p className="flex items-center gap-2">
              <Phone size={16} className="text-[#FF5E00]" />
              281-938-9601
            </p>
            <p className="flex items-center gap-2">
              <Mail size={16} className="text-[#FF5E00]" />
              Brandon@oplogman.com
            </p>
          </div>
        </div>
      </div>
      
      <div className="border-t border-white/20 mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-4">
          <p className="text-[#b8c9e2] text-sm">© {new Date().getFullYear()} Opulence Logistics Management Corp. All rights reserved.</p>
          <Link to="/privacy" className="text-[#b8c9e2] text-sm hover:text-[#FF5E00] transition-colors">Privacy & Terms</Link>
        </div>
        <div className="flex gap-4">
          <a href="#" className="text-[#b8c9e2] hover:text-[#FF5E00] transition-colors"><Linkedin size={20} /></a>
          <a href="#" className="text-[#b8c9e2] hover:text-[#FF5E00] transition-colors"><Twitter size={20} /></a>
        </div>
      </div>
    </div>
  </footer>;


// ============== HOME PAGE ==============
const HomePage = () => {
  const services = [
  { icon: Briefcase, title: 'Business Operations & Management', desc: 'Create structure within day-to-day operations' },
  { icon: Target, title: 'Project & Program Management', desc: 'From idea to execution — efficient project management' },
  { icon: FileText, title: 'Tax Preparation & Financial Support', desc: 'Professional tax and financial organization' },
  { icon: Monitor, title: 'Technical Assistance (NAV-GA)', desc: 'Digital systems implementation and support' },
  { icon: Film, title: 'Production & Entertainment Management', desc: 'Expert coordination for productions and events' },
  { icon: Truck, title: 'Logistics & Courier Operations', desc: 'Efficient logistics solutions for service businesses' }];


  const whyOLM = [
  { icon: Clock, text: 'Over 2 Decades of Experience' },
  { icon: Users, text: 'Multi-Industry Expertise' },
  { icon: Target, text: 'Proven Systems & Structure' },
  { icon: Award, text: 'Results-Driven Execution' },
  { icon: Shield, text: 'Trusted by Business Owners & Organizations' }];


  return (
    <main data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a3a6e] via-[#1a3a6e]/80 to-transparent z-10" />
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1764119129000-4fee87b8cc9c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBnbGFzcyUyMG9mZmljZSUyMGJ1aWxkaW5nJTIwZ29sZGVuJTIwaG91cnxlbnwwfHx8fDE3NzQ1MDYwMTN8MA&ixlib=rb-4.1.0&q=85)' }} />

        
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-3xl stagger-children">
            <p className="text-xs uppercase tracking-[0.2em] text-[#FF5E00] font-bold mb-6">Opulence Logistics Management Corp.</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium text-white mb-6 leading-tight">
              We Bring Structure, Strategy, and Execution to Your Business
            </h1>
            <p className="text-lg text-[#b8c9e2] mb-10 leading-relaxed">
              Helping entrepreneurs and organizations organize, grow, and scale efficiently.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/booking"
                className="bg-[#FF5E00] hover:bg-[#e05300] text-white px-8 py-4 font-medium transition-colors flex items-center justify-center gap-2"
                data-testid="hero-book-consultation">

                Book a Consultation <ArrowRight size={18} />
              </Link>
              <a
                href={OUTLOOK_BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="border border-white/20 hover:border-white/50 text-white px-8 py-4 font-medium transition-colors flex items-center justify-center gap-2"
                data-testid="hero-schedule-call">

                Schedule a Call <ExternalLink size={18} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Value Section */}
      <section className="py-24 bg-[#234b8a]" data-testid="value-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#FF5E00] font-bold mb-4">Why Businesses Choose OLM</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-white mb-6">
                We don't just advise — we help you execute.
              </h2>
              <p className="text-[#b8c9e2] leading-relaxed">
                At Opulence Logistics Management Corp., we specialize in turning ideas into structured, revenue-generating systems. Whether you're overwhelmed, growing fast, or stuck in operations, we step in to bring clarity, organization, and results.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {whyOLM.map((item, idx) =>
              <div key={idx} className="bg-[#2d5ba3] border border-white/20 p-6 flex items-center gap-4 hover:border-[#FF5E00]/50 transition-colors">
                  <item.icon className="text-[#FF5E00] flex-shrink-0" size={24} />
                  <span className="text-white text-sm font-medium">{item.text}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Services Snapshot */}
      <section className="py-24 bg-[#1a3a6e]" data-testid="services-snapshot">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-[#FF5E00] font-bold mb-4">What We Do</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-white">Our Core Services</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, idx) =>
            <div
              key={idx}
              className="bg-[#234b8a] border border-white/20 p-8 hover:-translate-y-1 hover:border-[#FF5E00]/50 transition-all duration-300 group"
              data-testid={`service-card-${idx}`}>

                <service.icon className="text-[#FF5E00] mb-4 group-hover:scale-110 transition-transform" size={32} />
                <h3 className="text-lg font-medium text-white mb-2">{service.title}</h3>
                <p className="text-[#b8c9e2] text-sm">{service.desc}</p>
              </div>
            )}
          </div>
          
          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-[#FF5E00] hover:text-white font-medium transition-colors"
              data-testid="learn-more-services">

              Learn More About Our Services <ChevronRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#234b8a] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-r from-[#FF5E00]/10 to-[#004AAD]/10" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-white mb-6">
            Ready to Bring Structure to Your Business?
          </h2>
          <p className="text-[#b8c9e2] mb-10 text-lg">
            Let's take your vision and turn it into results.
          </p>
          <Link
            to="/booking"
            className="inline-flex items-center gap-2 bg-[#FF5E00] hover:bg-[#e05300] text-white px-10 py-4 font-medium transition-colors"
            data-testid="cta-book-consultation">

            Book Your Consultation Today <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </main>);

};

// ============== ABOUT PAGE ==============
const AboutPage = () => {
  const background = [
  'Technical Support Manager – NAV-GA',
  'Project Manager – C & G Site Services, LLC',
  'Project Manager – Clarke and Associates LLP',
  'Coordinating Producer – Eyes of The Storm',
  'Stage Manager & Casting Director – Wanda Smith and Friends',
  'Operations Manager – LCS Tax Services & Financial Management',
  'Operations Manager – What\'s Ah Name Courier Express'];


  const expertise = [
  'Implement real systems',
  'Organize business operations',
  'Improve efficiency',
  'Drive measurable results'];


  return (
    <main className="pt-20" data-testid="about-page">
      {/* Hero */}
      <section className="py-24 bg-[#1a3a6e]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#FF5E00] font-bold mb-4">Meet The CEO</p>
              <h1 className="text-4xl sm:text-5xl tracking-tighter font-medium text-white mb-6">Brandon J. Simpson</h1>
              <p className="text-[#b8c9e2] leading-relaxed mb-6">
                Brandon J. Simpson is a dynamic operations and project management professional with over 2 decades of experience supporting businesses, productions, and organizations across multiple industries.
              </p>
              <p className="text-[#b8c9e2] leading-relaxed">
                As the CEO of Opulence Logistics Management Corp., Brandon has built a reputation for delivering structure, strategy, and execution to businesses that need clarity, organization, and growth.
              </p>
            </div>
            <div className="relative">
              <div className="aspect-[4/5] bg-[#234b8a] border border-white/20 overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_operations-hub-24/artifacts/0vnl8b4c_Brandon%20from%20Corey.png"
                  alt="Brandon J. Simpson"
                  className="w-full h-full object-cover object-top" />

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Sets Him Apart */}
      <section className="py-24 bg-[#234b8a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-[#FF5E00] font-bold mb-4">What Sets Him Apart</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-white mb-6">
              Brandon is not your average consultant.
            </h2>
            <p className="text-[#b8c9e2]">
              He doesn't just give advice — he works alongside clients to:
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {expertise.map((item, idx) =>
            <div key={idx} className="bg-[#2d5ba3] border border-white/20 p-8 text-center hover:border-[#FF5E00]/50 transition-colors">
                <CheckCircle className="text-[#FF5E00] mx-auto mb-4" size={32} />
                <p className="text-white font-medium">{item}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Professional Background */}
      <section className="py-24 bg-[#1a3a6e]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.2em] text-[#FF5E00] font-bold mb-4">Professional Background</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-white mb-12">Experience Across Industries</h2>
            
            <div className="space-y-4">
              {background.map((role, idx) =>
              <div key={idx} className="flex items-center gap-4 p-4 bg-[#234b8a] border border-white/20 hover:border-[#FF5E00]/50 transition-colors">
                  <div className="w-2 h-2 bg-[#FF5E00] rounded-full" />
                  <span className="text-white">{role}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-24 bg-[#234b8a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-[#FF5E00] font-bold mb-4">Our Mission</p>
          <blockquote className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-white leading-relaxed">
            "Our mission is to help businesses move from vision to revenue through strategic management, structure, and execution."
          </blockquote>
        </div>
      </section>
    </main>);

};

// ============== SERVICES PAGE ==============
const ServicesPage = () => {
  const services = [
  {
    icon: Briefcase,
    title: 'Business Operations & Management',
    description: 'We help business owners create structure within their day-to-day operations.',
    includes: ['Workflow Optimization', 'CRM Setup & Implementation', 'Administrative & Executive Support', 'Business Process Development'],
    bestFor: 'Entrepreneurs who feel overwhelmed or disorganized',
    color: '#FF5E00'
  },
  {
    icon: Target,
    title: 'Project & Program Management',
    description: 'From idea to execution — we manage your projects efficiently.',
    includes: ['Full Project Coordination', 'Timeline & Deliverable Management', 'Team & Vendor Coordination', 'Program Development'],
    bestFor: 'Businesses launching new initiatives or scaling operations',
    color: '#004AAD'
  },
  {
    icon: FileText,
    title: 'Tax & Financial Services',
    description: 'Professional tax preparation and financial organization support.',
    includes: ['Individual & Business Tax Preparation', 'IRS Annual Filing Season Program Participation', 'Financial Document Organization', 'Tax Planning Support'],
    bestFor: 'Individuals and business owners needing reliable tax services',
    color: '#FF5E00'
  },
  {
    icon: Monitor,
    title: 'Technical Assistance (NAV-GA)',
    description: 'Helping businesses implement and manage digital systems.',
    includes: ['Business Systems Support', 'Tech Setup & Troubleshooting', 'Digital Tools & Process Guidance'],
    bestFor: 'Businesses needing help with systems and technology',
    color: '#004AAD'
  },
  {
    icon: Film,
    title: 'Production & Entertainment Management',
    description: 'Experienced coordination for productions and live events.',
    includes: ['Stage Management', 'Casting Direction', 'Production Coordination', 'Event Logistics'],
    bestFor: 'Production teams and entertainment professionals',
    color: '#FF5E00'
  },
  {
    icon: Truck,
    title: 'Logistics & Operations Management',
    description: 'Efficient logistics solutions for service-based businesses.',
    includes: ['Courier Operations Management', 'Route Optimization', 'Team Coordination'],
    bestFor: 'Courier, delivery, and service businesses',
    color: '#004AAD'
  }];


  return (
    <main className="pt-20" data-testid="services-page">
      {/* Hero */}
      <section className="py-24 bg-[#1a3a6e]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-[#FF5E00] font-bold mb-4">What We Offer</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium text-white mb-6">Our Services</h1>
          <p className="text-[#b8c9e2] max-w-2xl mx-auto">
            Comprehensive solutions to help your business organize, grow, and scale efficiently.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-[#234b8a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {services.map((service, idx) =>
            <div
              key={idx}
              className={`grid lg:grid-cols-2 gap-8 p-8 bg-[#2d5ba3] border border-white/20 hover:border-white/20 transition-colors ${idx % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
              data-testid={`service-detail-${idx}`}>

                <div className={idx % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 bg-[#1a3a6e] border border-white/20" style={{ borderColor: `${service.color}30` }}>
                      <service.icon size={28} style={{ color: service.color }} />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-medium text-white">{service.title}</h3>
                  </div>
                  <p className="text-[#b8c9e2] mb-6">{service.description}</p>
                  
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-white mb-3">Services Include:</h4>
                    <ul className="space-y-2">
                      {service.includes.map((item, i) =>
                    <li key={i} className="flex items-center gap-2 text-[#b8c9e2] text-sm">
                          <CheckCircle size={16} style={{ color: service.color }} />
                          {item}
                        </li>
                    )}
                    </ul>
                  </div>
                  
                  <div className="p-4 bg-[#1a3a6e] border-l-2" style={{ borderColor: service.color }}>
                    <p className="text-sm">
                      <span className="text-white font-medium">Best For: </span>
                      <span className="text-[#b8c9e2]">{service.bestFor}</span>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#1a3a6e]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-[#b8c9e2] mb-10">
            Let's discuss which services are right for your business.
          </p>
          <Link
            to="/booking"
            className="inline-flex items-center gap-2 bg-[#FF5E00] hover:bg-[#e05300] text-white px-10 py-4 font-medium transition-colors"
            data-testid="services-cta-booking">

            Book a Consultation <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </main>);

};

// ============== BOOKING PAGE ==============
const BookingPage = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    business_name: '',
    email: '',
    phone: '',
    service_needed: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/booking`, formData);
      toast.success('Booking inquiry submitted! We\'ll be in touch soon.');
      setFormData({ full_name: '', business_name: '', email: '', phone: '', service_needed: '', description: '' });
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
  'Business Operations & Management',
  'Project & Program Management',
  'Tax & Financial Services',
  'Technical Assistance (NAV-GA)',
  'Production & Entertainment Management',
  'Logistics & Operations Management'];


  return (
    <main className="pt-20" data-testid="booking-page">
      {/* Hero */}
      <section className="py-24 bg-[#1a3a6e]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#FF5E00] font-bold mb-4">Book Now</p>
              <h1 className="text-4xl sm:text-5xl tracking-tighter font-medium text-white mb-6">Book a Consultation</h1>
              <p className="text-[#b8c9e2] mb-8">
                Let's discuss how we can bring structure and strategy to your business.
              </p>
              
              <div className="space-y-4 mb-8">
                <a
                  href={OUTLOOK_BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 p-6 bg-[#234b8a] border border-white/20 hover:border-[#FF5E00]/50 transition-colors group"
                  data-testid="booking-15min">

                  <div className="p-3 bg-[#FF5E00] group-hover:scale-110 transition-transform flex-shrink-0">
                    <Clock size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium">15-Min Introductory Consultation</h3>
                    <p className="text-[#b8c9e2] text-sm mt-1">A brief 15-minute introductory consultation to discuss your logistics needs and explore how Opulence Logistics Management Corp. can elevate your operations.</p>
                  </div>
                  <ExternalLink className="text-[#b8c9e2] ml-auto flex-shrink-0" size={20} />
                </a>
                
                <a
                  href={OUTLOOK_BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 p-6 bg-[#234b8a] border border-white/20 hover:border-[#004AAD]/50 transition-colors group"
                  data-testid="booking-30min-project">

                  <div className="p-3 bg-[#004AAD] group-hover:scale-110 transition-transform flex-shrink-0">
                    <Target size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium">30-Min Project Management Strategy Session</h3>
                    <p className="text-[#b8c9e2] text-sm mt-1">A focused 30-minute strategy session designed to assess, plan, and align your project management needs. Identify key objectives, address operational challenges, and develop a clear path forward.</p>
                  </div>
                  <ExternalLink className="text-[#b8c9e2] ml-auto flex-shrink-0" size={20} />
                </a>
                
                <a
                  href={OUTLOOK_BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 p-6 bg-[#234b8a] border border-white/20 hover:border-[#FF5E00]/50 transition-colors group"
                  data-testid="booking-30min-tax">

                  <div className="p-3 bg-[#FF5E00] group-hover:scale-110 transition-transform flex-shrink-0">
                    <FileText size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium">30-Min Tax Preparation Strategy Session</h3>
                    <p className="text-[#b8c9e2] text-sm mt-1">A dedicated 30-minute strategy session focused on your tax preparation needs. Review financial documents, identify key deductions, and develop a strategic plan for optimal outcomes.</p>
                  </div>
                  <ExternalLink className="text-[#b8c9e2] ml-auto flex-shrink-0" size={20} />
                </a>
              </div>
            </div>
            
            {/* Booking Form */}
            <div className="bg-[#234b8a] border border-white/20 p-8">
              <h3 className="text-xl font-medium text-white mb-6">Or Submit an Inquiry</h3>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="booking-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      required
                      className="w-full bg-[#1a3a6e] border border-white/20 text-white px-4 py-3 focus:ring-2 focus:ring-[#FF5E00] focus:border-transparent outline-none"
                      data-testid="booking-full-name" />

                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Business Name</label>
                    <input
                      type="text"
                      name="business_name"
                      value={formData.business_name}
                      onChange={handleChange}
                      className="w-full bg-[#1a3a6e] border border-white/20 text-white px-4 py-3 focus:ring-2 focus:ring-[#FF5E00] focus:border-transparent outline-none"
                      data-testid="booking-business-name" />

                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-[#1a3a6e] border border-white/20 text-white px-4 py-3 focus:ring-2 focus:ring-[#FF5E00] focus:border-transparent outline-none"
                      data-testid="booking-email" />

                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full bg-[#1a3a6e] border border-white/20 text-white px-4 py-3 focus:ring-2 focus:ring-[#FF5E00] focus:border-transparent outline-none"
                      data-testid="booking-phone" />

                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Service Needed *</label>
                  <select
                    name="service_needed"
                    value={formData.service_needed}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#1a3a6e] border border-white/20 text-white px-4 py-3 focus:ring-2 focus:ring-[#FF5E00] focus:border-transparent outline-none"
                    data-testid="booking-service">

                    <option value="">Select a service</option>
                    {services.map((service) =>
                    <option key={service} value={service}>{service}</option>
                    )}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Brief Description of Needs *</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="w-full bg-[#1a3a6e] border border-white/20 text-white px-4 py-3 focus:ring-2 focus:ring-[#FF5E00] focus:border-transparent outline-none resize-none"
                    data-testid="booking-description" />

                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#FF5E00] hover:bg-[#e05300] disabled:opacity-50 text-white px-6 py-4 font-medium transition-colors flex items-center justify-center gap-2"
                  data-testid="booking-submit">

                  {isSubmitting ? 'Submitting...' : 'Submit Inquiry'} <ArrowRight size={18} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </main>);

};

// ============== CONTACT PAGE ==============
const ContactPage = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent! We\'ll respond within 24 hours.');
      setFormData({ full_name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="pt-20" data-testid="contact-page">
      <section className="py-24 bg-[#1a3a6e]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#FF5E00] font-bold mb-4">Contact Us</p>
              <h1 className="text-4xl sm:text-5xl tracking-tighter font-medium text-white mb-6">Get In Touch</h1>
              <p className="text-[#b8c9e2] mb-12">
                We'll respond within 24 hours.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#234b8a] border border-white/20">
                    <MapPin className="text-[#FF5E00]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1">Office Address</h3>
                    <p className="text-[#b8c9e2]">1250 W Sam Houston Pkwy S<br />Suite 302<br />Houston, TX 77042</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#234b8a] border border-white/20">
                    <Phone className="text-[#FF5E00]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1">Phone</h3>
                    <p className="text-[#b8c9e2]">Office: 281-938-9601</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#234b8a] border border-white/20">
                    <Mail className="text-[#FF5E00]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1">Email</h3>
                    <a href="mailto:Brandon@oplogman.com" className="text-[#FF5E00] hover:underline">Brandon@oplogman.com</a>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Contact Form */}
            <div className="bg-[#234b8a] border border-white/20 p-8">
              <h3 className="text-xl font-medium text-white mb-6">Send a Message</h3>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    className="w-full bg-[#1a3a6e] border border-white/20 text-white px-4 py-3 focus:ring-2 focus:ring-[#FF5E00] focus:border-transparent outline-none"
                    data-testid="contact-full-name" />

                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-[#1a3a6e] border border-white/20 text-white px-4 py-3 focus:ring-2 focus:ring-[#FF5E00] focus:border-transparent outline-none"
                      data-testid="contact-email" />

                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full bg-[#1a3a6e] border border-white/20 text-white px-4 py-3 focus:ring-2 focus:ring-[#FF5E00] focus:border-transparent outline-none"
                      data-testid="contact-phone" />

                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full bg-[#1a3a6e] border border-white/20 text-white px-4 py-3 focus:ring-2 focus:ring-[#FF5E00] focus:border-transparent outline-none resize-none"
                    data-testid="contact-message" />

                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#FF5E00] hover:bg-[#e05300] disabled:opacity-50 text-white px-6 py-4 font-medium transition-colors flex items-center justify-center gap-2"
                  data-testid="contact-submit">

                  {isSubmitting ? 'Sending...' : 'Send Message'} <ArrowRight size={18} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </main>);

};

// ============== ADMIN LOGIN ==============
const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem('olm_admin_token', response.data.token);
      localStorage.setItem('olm_admin_email', response.data.email);
      window.location.href = '/admin/dashboard';
    } catch (error) {
      toast.error('Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="pt-20 min-h-screen flex items-center justify-center bg-[#1a3a6e]" data-testid="admin-login-page">
      <div className="w-full max-w-md p-8 bg-[#234b8a] border border-white/20">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="OLM Logo" className="h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-medium text-white">Admin Login</h1>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-login-form">
          <div>
            <label className="block text-sm font-medium text-white mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-[#1a3a6e] border border-white/20 text-white px-4 py-3 focus:ring-2 focus:ring-[#FF5E00] focus:border-transparent outline-none"
              data-testid="admin-email" />

          </div>
          
          <div>
            <label className="block text-sm font-medium text-white mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full bg-[#1a3a6e] border border-white/20 text-white px-4 py-3 focus:ring-2 focus:ring-[#FF5E00] focus:border-transparent outline-none"
              data-testid="admin-password" />

          </div>
          
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-[#FF5E00] hover:bg-[#e05300] disabled:opacity-50 text-white px-6 py-4 font-medium transition-colors"
            data-testid="admin-submit">

            {isLoading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </main>);

};

// ============== FINANCIALS SECTION ==============
const FinancialsSection = ({ token }) => {
  const [activeSubTab, setActiveSubTab] = useState('overview');
  
  const subTabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'invoices', label: 'Invoices' },
    { id: 'payments', label: 'Payments' },
    { id: 'bills', label: 'Bills & Expenses' },
    { id: 'baddebt', label: 'Bad Debt' },
    { id: 'pl', label: 'P&L Statement' },
  ];

  return (
    <div className="space-y-4">
      {/* Sub-navigation */}
      <div className="flex gap-1 bg-[#234b8a] border border-white/20 rounded-lg p-1">
        {subTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
              activeSubTab === tab.id
                ? 'bg-[#C8952A] text-white'
                : 'text-[#b8c9e2] hover:text-white hover:bg-white/10'
            }`}
            data-testid={`financial-tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeSubTab === 'overview' && <FinancialDashboard token={token} />}
      {activeSubTab === 'invoices' && <InvoiceManagement token={token} />}
      {activeSubTab === 'payments' && <PaymentsReceived token={token} />}
      {activeSubTab === 'bills' && <BillsExpenses token={token} />}
      {activeSubTab === 'baddebt' && <BadDebtTracker token={token} />}
      {activeSubTab === 'pl' && <PLStatement token={token} />}
    </div>
  );
};

// ============== ADMIN DASHBOARD ==============
const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [contacts, setContacts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const token = localStorage.getItem('olm_admin_token');
  const adminEmail = localStorage.getItem('olm_admin_email');

  useEffect(() => {
    if (!token) {
      window.location.href = '/admin';
      return;
    }
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [contactsRes, bookingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/contacts`, { headers }),
        axios.get(`${API}/admin/bookings`, { headers }),
        axios.get(`${API}/admin/stats`, { headers })
      ]);
      setContacts(contactsRes.data);
      setBookings(bookingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('olm_admin_token');
        window.location.href = '/admin';
      }
      toast.error('Failed to fetch data');
    } finally {
      setIsLoading(false);
    }
  };

  const updateStatus = async (type, id, status) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/${type}/${id}/status`, { status }, { headers });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteItem = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/${type}/${id}`, { headers });
      toast.success('Item deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const convertToClient = async (contactId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/contacts/${contactId}/convert-to-client`, {}, { headers });
      toast.success('Contact converted to client!');
      fetchData();
    } catch (error) {
      toast.error('Failed to convert contact');
    }
  };

  const logout = () => {
    localStorage.removeItem('olm_admin_token');
    localStorage.removeItem('olm_admin_email');
    window.location.href = '/admin';
  };

  const statusColors = {
    new: 'bg-green-500/20 text-green-400',
    pending: 'bg-yellow-500/20 text-yellow-400',
    contacted: 'bg-blue-500/20 text-blue-400',
    completed: 'bg-gray-500/20 text-gray-400',
    converted: 'bg-purple-500/20 text-purple-400'
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'pipeline', label: 'Pipeline', icon: PieChart },
    { id: 'clients', label: 'Clients', icon: Users },
    { id: 'vendors', label: 'Vendors', icon: Building2 },
    { id: 'tasks', label: 'Tasks', icon: ListTodo },
    { id: 'activities', label: 'Activities', icon: Activity },
    { id: 'contacts', label: 'Contacts', icon: Mail },
    { id: 'bookings', label: 'Bookings', icon: Briefcase },
    { id: 'financials', label: 'Financials', icon: DollarSign },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#1a3a6e] flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-[#1a3a6e]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[#234b8a] border-b border-white/20 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="OLM Logo" className="h-10" />
            <span className="text-white font-medium">CRM Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-[#b8c9e2] text-sm">{adminEmail}</span>
            <button
              onClick={logout}
              className="text-sm text-[#FF5E00] hover:underline"
              data-testid="admin-logout"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-[#234b8a] border-r border-white/20 min-h-[calc(100vh-73px)] p-4">
          <nav className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded transition-colors ${
                  activeTab === tab.id 
                    ? 'bg-[#FF5E00] text-white' 
                    : 'text-[#b8c9e2] hover:text-white hover:bg-white/10'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <div className="flex-1 p-6">
          {activeTab === 'dashboard' && <CRMDashboard token={token} />}
          {activeTab === 'pipeline' && <PipelineView token={token} />}
          {activeTab === 'clients' && <ClientsList token={token} />}
          {activeTab === 'vendors' && <VendorDirectory token={token} />}
          {activeTab === 'tasks' && <TasksList token={token} />}
          {activeTab === 'activities' && <ActivitiesList token={token} />}
          
          {activeTab === 'contacts' && (
            <div>
              <h2 className="text-xl font-medium text-white mb-6">Website Contacts</h2>
              <div className="bg-[#234b8a] border border-white/20 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="contacts-table">
                    <thead className="bg-[#1a3a6e]">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-medium text-[#b8c9e2]">Name</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-[#b8c9e2]">Email</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-[#b8c9e2]">Phone</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-[#b8c9e2]">Message</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-[#b8c9e2]">Status</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-[#b8c9e2]">Date</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-[#b8c9e2]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.map((contact) => (
                        <tr key={contact.id} className="border-t border-white/10">
                          <td className="px-4 py-3 text-white">{contact.full_name}</td>
                          <td className="px-4 py-3 text-[#b8c9e2]">{contact.email}</td>
                          <td className="px-4 py-3 text-[#b8c9e2]">{contact.phone}</td>
                          <td className="px-4 py-3 text-[#b8c9e2] max-w-xs truncate">{contact.message}</td>
                          <td className="px-4 py-3">
                            <select
                              value={contact.status}
                              onChange={(e) => updateStatus('contacts', contact.id, e.target.value)}
                              className={`px-2 py-1 text-sm ${statusColors[contact.status]} bg-transparent border-none cursor-pointer rounded`}
                            >
                              <option value="new">New</option>
                              <option value="contacted">Contacted</option>
                              <option value="completed">Completed</option>
                              <option value="converted">Converted</option>
                            </select>
                          </td>
                          <td className="px-4 py-3 text-[#b8c9e2] text-sm">
                            {new Date(contact.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {contact.status !== 'converted' && (
                                <button
                                  onClick={() => convertToClient(contact.id)}
                                  className="text-[#FF5E00] hover:text-[#e05300] text-xs font-medium"
                                >
                                  → Client
                                </button>
                              )}
                              <button
                                onClick={() => deleteItem('contacts', contact.id)}
                                className="text-red-400 hover:text-red-300 text-sm"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {contacts.length === 0 && (
                        <tr>
                          <td colSpan={7} className="px-4 py-8 text-center text-[#b8c9e2]">No contacts yet</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'bookings' && (
            <div>
              <h2 className="text-xl font-medium text-white mb-6">Booking Inquiries</h2>
              <div className="bg-[#234b8a] border border-white/20 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="bookings-table">
                    <thead className="bg-[#1a3a6e]">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-medium text-[#b8c9e2]">Name</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-[#b8c9e2]">Business</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-[#b8c9e2]">Email</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-[#b8c9e2]">Service</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-[#b8c9e2]">Status</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-[#b8c9e2]">Date</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-[#b8c9e2]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map((booking) => (
                        <tr key={booking.id} className="border-t border-white/10">
                          <td className="px-4 py-3 text-white">{booking.full_name}</td>
                          <td className="px-4 py-3 text-[#b8c9e2]">{booking.business_name || '-'}</td>
                          <td className="px-4 py-3 text-[#b8c9e2]">{booking.email}</td>
                          <td className="px-4 py-3 text-[#b8c9e2]">{booking.service_needed}</td>
                          <td className="px-4 py-3">
                            <select
                              value={booking.status}
                              onChange={(e) => updateStatus('bookings', booking.id, e.target.value)}
                              className={`px-2 py-1 text-sm ${statusColors[booking.status]} bg-transparent border-none cursor-pointer rounded`}
                            >
                              <option value="pending">Pending</option>
                              <option value="contacted">Contacted</option>
                              <option value="completed">Completed</option>
                            </select>
                          </td>
                          <td className="px-4 py-3 text-[#b8c9e2] text-sm">
                            {new Date(booking.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => deleteItem('bookings', booking.id)}
                              className="text-red-400 hover:text-red-300 text-sm"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                      {bookings.length === 0 && (
                        <tr>
                          <td colSpan={7} className="px-4 py-8 text-center text-[#b8c9e2]">No bookings yet</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'financials' && (
            <FinancialsSection token={token} />
          )}
        </div>
      </div>
    </main>
  );
};

// ============== PROTECTED ROUTE ==============
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('olm_admin_token');
  if (!token) {
    return <Navigate to="/admin" replace />;
  }
  return children;
};

// ============== MAIN APP ==============
// ============== PRIVACY & TERMS PAGE ==============
const PrivacyPage = () => {
  return (
    <main className="pt-20" data-testid="privacy-page">
      <section className="py-24 bg-[#1a3a6e]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl tracking-tighter font-medium text-white mb-8">Privacy Policy & Terms of Service</h1>
          <p className="text-[#b8c9e2] mb-8">Last updated: March 2026</p>
          
          <div className="prose prose-invert max-w-none space-y-8">
            {/* Privacy Policy */}
            <div className="bg-[#234b8a] border border-white/20 p-8 rounded-lg">
              <h2 className="text-2xl font-medium text-[#C8952A] mb-6">Privacy Policy</h2>
              
              <div className="space-y-6 text-[#b8c9e2]">
                <div>
                  <h3 className="text-white font-medium mb-2">1. Information We Collect</h3>
                  <p>Opulence Logistics Management Corp. ("OLM," "we," "us," or "our") collects information you provide directly to us, including:</p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Name, email address, phone number, and company information</li>
                    <li>Information about your business needs and project requirements</li>
                    <li>Payment information when you engage our services</li>
                    <li>Communications you send to us</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-white font-medium mb-2">2. How We Use Your Information</h3>
                  <p>We use the information we collect to:</p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Provide, maintain, and improve our consulting services</li>
                    <li>Process transactions and send related information</li>
                    <li>Send you technical notices, updates, and support messages</li>
                    <li>Respond to your comments, questions, and requests</li>
                    <li>Communicate about services, offers, and events</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-white font-medium mb-2">3. Information Sharing</h3>
                  <p>We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:</p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>With your consent or at your direction</li>
                    <li>With service providers who assist in our operations</li>
                    <li>To comply with legal obligations</li>
                    <li>To protect our rights, privacy, safety, or property</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-white font-medium mb-2">4. Data Security</h3>
                  <p>We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.</p>
                </div>
                
                <div>
                  <h3 className="text-white font-medium mb-2">5. Your Rights</h3>
                  <p>You have the right to access, correct, or delete your personal information. To exercise these rights, please contact us at Brandon@oplogman.com.</p>
                </div>
              </div>
            </div>
            
            {/* Terms of Service */}
            <div className="bg-[#234b8a] border border-white/20 p-8 rounded-lg">
              <h2 className="text-2xl font-medium text-[#C8952A] mb-6">Terms of Service</h2>
              
              <div className="space-y-6 text-[#b8c9e2]">
                <div>
                  <h3 className="text-white font-medium mb-2">1. Acceptance of Terms</h3>
                  <p>By accessing or using the services of Opulence Logistics Management Corp., you agree to be bound by these Terms of Service and our Privacy Policy.</p>
                </div>
                
                <div>
                  <h3 className="text-white font-medium mb-2">2. Services</h3>
                  <p>OLM provides business consulting services including but not limited to:</p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Business Consulting & Operations Support</li>
                    <li>Staffing & Talent Acquisition</li>
                    <li>Production Management Services</li>
                    <li>Technology & Digital Solutions</li>
                    <li>Film & Entertainment Logistics</li>
                    <li>Freight & Transportation Consulting</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-white font-medium mb-2">3. Payment Terms</h3>
                  <p>Payment terms will be specified in individual service agreements. Invoices are due upon receipt unless otherwise specified. Late payments may be subject to interest charges.</p>
                </div>
                
                <div>
                  <h3 className="text-white font-medium mb-2">4. Confidentiality</h3>
                  <p>Both parties agree to maintain the confidentiality of proprietary information shared during the course of our engagement.</p>
                </div>
                
                <div>
                  <h3 className="text-white font-medium mb-2">5. Limitation of Liability</h3>
                  <p>OLM's liability shall be limited to the fees paid for services. We shall not be liable for any indirect, incidental, special, or consequential damages.</p>
                </div>
                
                <div>
                  <h3 className="text-white font-medium mb-2">6. Governing Law</h3>
                  <p>These terms shall be governed by the laws of the State of Texas, without regard to its conflict of law provisions.</p>
                </div>
                
                <div>
                  <h3 className="text-white font-medium mb-2">7. Contact Information</h3>
                  <p>For questions about these Terms or our Privacy Policy, please contact:</p>
                  <div className="mt-2">
                    <p><strong className="text-white">Opulence Logistics Management Corp.</strong></p>
                    <p>1250 W Sam Houston Pkwy S, Suite 302</p>
                    <p>Houston, TX 77042</p>
                    <p>Email: Brandon@oplogman.com</p>
                    <p>Phone: 281-938-9601</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<><Navigation /><HomePage /><Footer /></>} />
          <Route path="/about" element={<><Navigation /><AboutPage /><Footer /></>} />
          <Route path="/services" element={<><Navigation /><ServicesPage /><Footer /></>} />
          <Route path="/booking" element={<><Navigation /><BookingPage /><Footer /></>} />
          <Route path="/contact" element={<><Navigation /><ContactPage /><Footer /></>} />
          <Route path="/privacy" element={<><Navigation /><PrivacyPage /><Footer /></>} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        </Routes>
      </BrowserRouter>
    </div>);

}

export default App;